import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  ClampTrigger,
  type ClampControlProps,
  resolveClampContentIds,
  useClampControl,
} from '@/hen-ui/hooks/useClampControl';
import { Flex } from '@/hen-ui/composites/Flex';
import { INLINE_COPY_HOST_CLASS } from '@/hen-ui/lib/inline-copy';
import { cn } from '@/hen-ui/utils';
import {
  lineClampVariants,
  preservedLineBreaks,
  type LineClampVariantProps,
} from './line-clamp.variants';

/** Maps the author-facing alignment vocabulary onto Flex's main-axis values. */
const TRIGGER_JUSTIFY = {
  left: 'start',
  center: 'center',
  right: 'end',
} as const;

export interface LineClampProps
  extends React.HTMLAttributes<HTMLDivElement>,
    LineClampVariantProps,
    ClampControlProps {
  asChild?: boolean;
  /** Alignment of the expansion control, independent of the clamped content. */
  triggerAlign?: keyof typeof TRIGGER_JUSTIFY;
}

/**
 * Clamps overflowing content to a fixed number of lines and provides an
 * accessible in-place expansion control.
 *
 * Expansion is opt-in (`expandable` defaults to false). When expansion is on,
 * the component renders as a fragment (clamped content plus a sibling control)
 * and should not be styled or measured as a single element. Clamp-trigger
 * Buttons and Links ignore `asChild` and render a native button.
 */
export const LineClamp = React.forwardRef<HTMLDivElement, LineClampProps>(
  (
    {
      className,
      id,
      lines = 'none',
      asChild = false,
      children,
      trigger,
      collapsedLabel,
      expandedLabel,
      expandable,
      triggerAlign = 'left',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';
    const { contentId, contentRef, expanded, showTrigger, toggleExpanded } =
      useClampControl({
        lines,
        content: children,
        forwardedRef: ref,
        expandable,
        measureSelector: `.${INLINE_COPY_HOST_CLASS}`,
      });
    const { triggerContentId, renderedContentId } = resolveClampContentIds({
      asChild,
      children,
      id,
      contentId,
      showTrigger,
    });
    const contentElement = (
      <Comp
        ref={contentRef}
        {...(renderedContentId ? { id: renderedContentId } : {})}
        className={cn(
          expanded && preservedLineBreaks,
          lineClampVariants({ lines: expanded ? 'none' : lines }),
          className,
        )}
        {...props}
        data-hen-id={COMPONENT_IDS.LineClamp}
      >
        {children}
      </Comp>
    );

    if (!showTrigger) return contentElement;

    return (
      <>
        {contentElement}
        <Flex
          direction='row'
          gap='none'
          width='full'
          justify={TRIGGER_JUSTIFY[triggerAlign]}
        >
          <ClampTrigger
            trigger={trigger}
            collapsedLabel={collapsedLabel}
            expandedLabel={expandedLabel}
            contentId={triggerContentId}
            expanded={expanded}
            onToggle={toggleExpanded}
          />
        </Flex>
      </>
    );
  },
);

LineClamp.displayName = 'LineClamp';
